import { UntypedFormGroup } from '@angular/forms';

export interface ReportBlockCellLike {
  value?: number | null;
  source?: string | null;
}

export interface ReportRowLike {
  row_key?: string | null;
  has_children?: boolean | null;
  force_expand_children?: boolean | null;
  row_kind?: string | null;
  station_id?: number | null;
  parent_row_key?: string | null;
  row_level?: number | null;
}

export interface ReportBlockValueLike {
  value?: number | null;
}

export interface ReportBlockRowLike {
  blocks: ReportBlockValueLike[];
}

export interface ReportHierarchyNodeLike<T extends ReportRowLike> {
  row: T;
  children: ReportHierarchyNodeLike<T>[];
}

export interface ReportGroupedHierarchyRowLike extends ReportRowLike {
  owner?: string | null;
  source_category?: string | null;
  sub_category?: string | null;
}

interface ReportHierarchySubGroupLike<T extends ReportGroupedHierarchyRowLike> {
  label: string;
  rows: T[];
}

interface ReportHierarchySourceGroupLike<T extends ReportGroupedHierarchyRowLike> {
  label: string;
  subGroups: Map<string, ReportHierarchySubGroupLike<T>>;
  directRows: T[];
}

interface ReportHierarchySectorGroupLike<T extends ReportGroupedHierarchyRowLike> {
  label: string;
  sourceGroups: Map<string, ReportHierarchySourceGroupLike<T>>;
  footerRows: T[];
}

interface ReportHierarchySourceChildren<T extends ReportGroupedHierarchyRowLike> {
  children: ReportHierarchyNodeLike<T>[];
  directLeafNodes: ReportHierarchyNodeLike<T>[];
  hasFuelGroups: boolean;
  subNodes: ReportHierarchyNodeLike<T>[];
}

export interface BuildGroupedReportHierarchyConfig<T extends ReportGroupedHierarchyRowLike> {
  rows: T[];
  isSummaryView: boolean;
  normalizeHierarchyLabel: (value: string | null | undefined) => string;
  normalizeOptionalHierarchyLabel: (value: string | null | undefined) => string | null;
  isFuelHierarchyLabel: (value: string | null | undefined) => boolean;
  isMissingHierarchyLabel: (value: string | null | undefined) => boolean;
  sortNodes: (nodes: ReportHierarchyNodeLike<T>[]) => ReportHierarchyNodeLike<T>[];
  buildAggregateRow: (
    rowKey: string,
    label: string,
    rows: T[],
    rowLevel: number,
    rowKind: 'sector-total' | 'ipp-total' | 'source-total' | 'subcat-total',
    hasChildren: boolean,
    forceExpandChildren?: boolean,
  ) => T;
}

export interface ReportDateConversionLike {
  data_date: string;
  bs_date: string;
  bs_date_label: string;
  selected_date: string;
  selected_date_label: string;
}

export interface ReportBsCalendarCellLike {
  bs_date: string;
  bs_date_label: string;
  ad_date: string;
  in_current_month: boolean;
  is_today: boolean;
  is_selected: boolean;
}

export interface ReportBsCalendarLike {
  data_date: string;
  bs_date: string;
  bs_year: number;
  bs_month: number;
  bs_month_name: string;
  selected_date: string;
  selected_date_label: string;
  cells: ReportBsCalendarCellLike[];
}

export interface ReportOptionLike {
  id?: number | string | null;
  name?: string | null;
  station_code?: string | null;
}

export interface ReportBlockDefinitionLike {
  period: string;
}

export interface ReportBlockGroupLike<TPeriod extends string = string> {
  period: TPeriod;
  label: string;
  span: number;
}

export interface ReportHierarchyOrderRowLike {
  row_kind?: string | null;
  row_key?: string | null;
  station_name?: string | null;
}

export interface ReportAutocompleteSearchConfig<TOption extends ReportOptionLike> {
  query: unknown;
  form: UntypedFormGroup;
  controlName: string;
  normalizeNumber: NormalizeNumberFn;
  filterOptions: (query: string) => void;
  findOption: (label: string) => TOption | null;
}

export interface ReportAutocompleteCommitConfig<TOption extends ReportOptionLike> {
  searchTerm: unknown;
  form: UntypedFormGroup;
  controlName: string;
  normalizeNumber: NormalizeNumberFn;
  findOption: (label: string) => TOption | null;
  setSearchTerm: (value: string) => void;
}

export type NormalizeNumberFn = (value: unknown) => number | null;

export function formatReportNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '-';
  }

  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export function getReportCellText(cell: ReportBlockCellLike): string {
  return cell.value === null || cell.value === undefined ? '_' : formatReportNumber(cell.value);
}

export function getReportDateLabel(displayLabel: string, requestValue: string, adValue: string): string {
  return displayLabel || requestValue || adValue;
}

export function buildGeneratorReportHeaderSummary(rowCount: number, blockCount: number, blockLabel: string): string {
  return `Showing ${rowCount} generator${rowCount === 1 ? '' : 's'} â€¢ ${blockCount} ${blockLabel}${blockCount === 1 ? '' : 's'}`;
}

export function getNullableNumberValue(value: number | null | undefined): number | null {
  return value === null || value === undefined ? null : Number(value);
}

export function buildEditDataFormValue(value: number | null | undefined, remarks: string | null | undefined) {
  return {
    data_value: value === null || value === undefined ? '' : String(value),
    copy_previous_block_value: false,
    carry_over_previous_day_values: false,
    remarks: remarks ?? '',
  };
}

export function isLogsheetDataDetailCell(cell: ReportBlockCellLike): boolean {
  return cell.source === 'logsheet_data_details';
}

export function toggleReportHierarchyRow(row: ReportRowLike, expandedRowKeys: Set<string>): boolean {
  if (!row.row_key || !row.has_children) {
    return false;
  }

  if (expandedRowKeys.has(row.row_key)) {
    expandedRowKeys.delete(row.row_key);
  } else {
    expandedRowKeys.add(row.row_key);
  }

  return true;
}

export function buildLinkedReportHierarchyNodes<T extends ReportRowLike>(
  rows: T[],
  sortNodes: (nodes: ReportHierarchyNodeLike<T>[]) => ReportHierarchyNodeLike<T>[],
): ReportHierarchyNodeLike<T>[] {
  const nodeMap = new Map<string, ReportHierarchyNodeLike<T>>();
  const rootNodes: ReportHierarchyNodeLike<T>[] = [];

  for (const row of rows) {
    const rowKey = row.row_key ?? `${row.row_kind ?? 'data'}-${row.station_id ?? 0}`;
    nodeMap.set(rowKey, { row: { ...row, row_key: rowKey }, children: [] });
  }

  for (const node of nodeMap.values()) {
    const parentKey = node.row.parent_row_key;
    const parentNode = parentKey ? nodeMap.get(parentKey) : null;
    if (parentNode) {
      parentNode.children.push(node);
      continue;
    }

    rootNodes.push(node);
  }

  for (const node of nodeMap.values()) {
    if (node.children.length) {
      node.row.has_children = true;
      node.children = sortNodes(node.children);
    }
  }

  return rootNodes;
}

export function buildGroupedReportHierarchyNodes<T extends ReportGroupedHierarchyRowLike>(
  config: BuildGroupedReportHierarchyConfig<T>,
): ReportHierarchyNodeLike<T>[] {
  const { sectorGroupMap, sectorLabels } = groupReportHierarchyRows(config);
  const sectorNodes: ReportHierarchyNodeLike<T>[] = [];

  for (const sectorLabel of sectorLabels) {
    const sectorGroup = sectorGroupMap.get(sectorLabel);
    if (!sectorGroup) {
      continue;
    }

    sectorNodes.push(...buildReportHierarchySectorRootNodes(sectorLabel, sectorGroup, config));
  }

  return config.sortNodes(sectorNodes);
}

function groupReportHierarchyRows<T extends ReportGroupedHierarchyRowLike>(
  config: BuildGroupedReportHierarchyConfig<T>,
): {
  sectorGroupMap: Map<string, ReportHierarchySectorGroupLike<T>>;
  sectorLabels: string[];
} {
  const sectorGroupMap = new Map<string, ReportHierarchySectorGroupLike<T>>();
  const sectorLabels: string[] = [];

  for (const row of config.rows) {
    addReportHierarchyRowToGroups(sectorGroupMap, sectorLabels, row, config);
  }

  return { sectorGroupMap, sectorLabels };
}

function addReportHierarchyRowToGroups<T extends ReportGroupedHierarchyRowLike>(
  sectorGroupMap: Map<string, ReportHierarchySectorGroupLike<T>>,
  sectorLabels: string[],
  row: T,
  config: BuildGroupedReportHierarchyConfig<T>,
): void {
  const sectorLabel = config.normalizeHierarchyLabel(row.owner);
  const sourceLabel = config.normalizeHierarchyLabel(row.source_category);
  const subLabel = config.normalizeOptionalHierarchyLabel(row.sub_category);
  const sectorGroup = getOrCreateReportHierarchySectorGroup(sectorGroupMap, sectorLabels, sectorLabel);
  if (row.row_kind === 'power-small' || row.parent_row_key) {
    sectorGroup.footerRows.push(row);
    return;
  }

  const sourceGroup = getOrCreateReportHierarchySourceGroup(sectorGroup, sourceLabel);

  if (subLabel) {
    const subGroup = getOrCreateReportHierarchySubGroup(sourceGroup, subLabel);
    subGroup.rows.push(row);
    return;
  }

  sourceGroup.directRows.push(row);
}

function getOrCreateReportHierarchySectorGroup<T extends ReportGroupedHierarchyRowLike>(
  sectorGroupMap: Map<string, ReportHierarchySectorGroupLike<T>>,
  sectorLabels: string[],
  sectorLabel: string,
): ReportHierarchySectorGroupLike<T> {
  const existingGroup = sectorGroupMap.get(sectorLabel);
  if (existingGroup) {
    return existingGroup;
  }

  const newGroup: ReportHierarchySectorGroupLike<T> = {
    label: sectorLabel,
    sourceGroups: new Map<string, ReportHierarchySourceGroupLike<T>>(),
    footerRows: [],
  };
  sectorGroupMap.set(sectorLabel, newGroup);
  sectorLabels.push(sectorLabel);
  return newGroup;
}

function getOrCreateReportHierarchySourceGroup<T extends ReportGroupedHierarchyRowLike>(
  sectorGroup: ReportHierarchySectorGroupLike<T>,
  sourceLabel: string,
): ReportHierarchySourceGroupLike<T> {
  const existingGroup = sectorGroup.sourceGroups.get(sourceLabel);
  if (existingGroup) {
    return existingGroup;
  }

  const newGroup: ReportHierarchySourceGroupLike<T> = {
    label: sourceLabel,
    subGroups: new Map<string, ReportHierarchySubGroupLike<T>>(),
    directRows: [],
  };
  sectorGroup.sourceGroups.set(sourceLabel, newGroup);
  return newGroup;
}

function getOrCreateReportHierarchySubGroup<T extends ReportGroupedHierarchyRowLike>(
  sourceGroup: ReportHierarchySourceGroupLike<T>,
  subLabel: string,
): ReportHierarchySubGroupLike<T> {
  const existingGroup = sourceGroup.subGroups.get(subLabel);
  if (existingGroup) {
    return existingGroup;
  }

  const newGroup: ReportHierarchySubGroupLike<T> = {
    label: subLabel,
    rows: [],
  };
  sourceGroup.subGroups.set(subLabel, newGroup);
  return newGroup;
}

function buildReportHierarchySectorRootNodes<T extends ReportGroupedHierarchyRowLike>(
  sectorLabel: string,
  sectorGroup: ReportHierarchySectorGroupLike<T>,
  config: BuildGroupedReportHierarchyConfig<T>,
): ReportHierarchyNodeLike<T>[] {
  if (shouldLiftReportFooterRows(sectorLabel, sectorGroup, config)) {
    return buildLinkedReportHierarchyNodes(sectorGroup.footerRows, config.sortNodes);
  }

  return [buildReportHierarchySectorNode(sectorLabel, sectorGroup, config)];
}

function shouldLiftReportFooterRows<T extends ReportGroupedHierarchyRowLike>(
  sectorLabel: string,
  sectorGroup: ReportHierarchySectorGroupLike<T>,
  config: BuildGroupedReportHierarchyConfig<T>,
): boolean {
  const normalizedLabel = sectorLabel.trim().toLowerCase();
  return (
    (config.isMissingHierarchyLabel(sectorLabel) || normalizedLabel === 'unassigned') &&
    sectorGroup.sourceGroups.size === 0 &&
    sectorGroup.footerRows.length > 0
  );
}

function buildReportHierarchySectorNode<T extends ReportGroupedHierarchyRowLike>(
  sectorLabel: string,
  sectorGroup: ReportHierarchySectorGroupLike<T>,
  config: BuildGroupedReportHierarchyConfig<T>,
): ReportHierarchyNodeLike<T> {
  const sourceNodes: ReportHierarchyNodeLike<T>[] = [];
  const sectorRows = [
    ...Array.from(sectorGroup.sourceGroups.values()).flatMap((group) => [
      ...group.directRows,
      ...Array.from(group.subGroups.values()).flatMap((subGroup) => subGroup.rows),
    ]),
    ...sectorGroup.footerRows.filter((row) => !row.parent_row_key && row.row_kind !== 'power-small'),
  ];

  for (const [sourceLabel, sourceGroup] of sectorGroup.sourceGroups.entries()) {
    sourceNodes.push(buildReportHierarchySourceNode(sectorLabel, sourceLabel, sourceGroup, config));
  }
  const footerNodes = buildLinkedReportHierarchyNodes(sectorGroup.footerRows, config.sortNodes);

  const sectorRow = config.buildAggregateRow(
    `sector-total-${slugifyReportLabel(sectorLabel)}`,
    `Total ${sectorLabel}`,
    sectorRows,
    0,
    'sector-total',
    true,
    config.isSummaryView,
  );

  return {
    row: sectorRow,
    children: [...sourceNodes, ...footerNodes],
  };
}

function buildReportHierarchySourceNode<T extends ReportGroupedHierarchyRowLike>(
  sectorLabel: string,
  sourceLabel: string,
  sourceGroup: ReportHierarchySourceGroupLike<T>,
  config: BuildGroupedReportHierarchyConfig<T>,
): ReportHierarchyNodeLike<T> {
  const { children, directLeafNodes, hasFuelGroups, subNodes } = buildReportHierarchySourceChildren(
    sectorLabel,
    sourceLabel,
    sourceGroup,
    config,
  );
  const sourceRows = [
    ...directLeafNodes.map((node) => node.row),
    ...subNodes.flatMap((node) => node.children.map((child) => child.row)),
  ];

  const sourceRow = config.buildAggregateRow(
    `source-total-${slugifyReportLabel(sectorLabel)}-${slugifyReportLabel(sourceLabel)}`,
    `Total ${sourceLabel}`,
    sourceRows,
    1,
    'source-total',
    hasFuelGroups || directLeafNodes.length > 0,
    config.isSummaryView && hasFuelGroups,
  );

  return {
    row: sourceRow,
    children,
  };
}

function buildReportHierarchySourceChildren<T extends ReportGroupedHierarchyRowLike>(
  sectorLabel: string,
  sourceLabel: string,
  sourceGroup: ReportHierarchySourceGroupLike<T>,
  config: BuildGroupedReportHierarchyConfig<T>,
): ReportHierarchySourceChildren<T> {
  const subGroups = Array.from(sourceGroup.subGroups.entries());
  const fuelSubGroups = subGroups.filter(([, group]) => config.isFuelHierarchyLabel(group.label));
  const directLeafRows = [
    ...sourceGroup.directRows,
    ...subGroups
      .filter(([, group]) => !config.isFuelHierarchyLabel(group.label))
      .flatMap(([, group]) => group.rows),
  ];
  const subNodes = fuelSubGroups.map(([subLabel, subGroup]) =>
    buildReportHierarchySubNode(sectorLabel, sourceLabel, subLabel, subGroup, config),
  );
  const directLeafNodes = directLeafRows.map((leafRow) => ({
    row: leafRow,
    children: [],
  }));
  const hasFuelGroups = fuelSubGroups.length > 0;
  let children: ReportHierarchyNodeLike<T>[];

  if (config.isSummaryView) {
    children = hasFuelGroups ? subNodes : directLeafNodes;
  } else if (hasFuelGroups) {
    children = [...subNodes, ...directLeafNodes];
  } else {
    children = directLeafNodes;
  }

  return {
    children,
    directLeafNodes,
    hasFuelGroups,
    subNodes,
  };
}

function buildReportHierarchySubNode<T extends ReportGroupedHierarchyRowLike>(
  sectorLabel: string,
  sourceLabel: string,
  subLabel: string,
  subGroup: ReportHierarchySubGroupLike<T>,
  config: BuildGroupedReportHierarchyConfig<T>,
): ReportHierarchyNodeLike<T> {
  const subRow = config.buildAggregateRow(
    `subcat-total-${slugifyReportLabel(sectorLabel)}-${slugifyReportLabel(sourceLabel)}-${slugifyReportLabel(subLabel)}`,
    `Total ${subLabel}`,
    subGroup.rows,
    2,
    'subcat-total',
    true,
    false,
  );

  return {
    row: subRow,
    children: subGroup.rows.map((leafRow) => ({
      row: leafRow,
      children: [],
    })),
  };
}

export function flattenReportHierarchyNodes<T extends ReportRowLike>(
  nodes: ReportHierarchyNodeLike<T>[],
  expandedKeys: Set<string>,
  shouldForceExpand: (node: ReportHierarchyNodeLike<T>) => boolean = () => false,
): T[] {
  const rows: T[] = [];

  for (const node of nodes) {
    rows.push(node.row);
    const rowKey = node.row.row_key;
    if (node.row.has_children && rowKey && (shouldForceExpand(node) || expandedKeys.has(rowKey))) {
      rows.push(...flattenReportHierarchyNodes(node.children, expandedKeys, shouldForceExpand));
    }
  }

  return rows;
}

export function getReportRowBlockExtremeValue(row: ReportBlockRowLike, mode: 'max' | 'min'): number | null {
  const values = row.blocks
    .map((cell) => cell.value)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

  if (!values.length) {
    return null;
  }

  const extremeValue = mode === 'max' ? Math.max(...values) : Math.min(...values);
  return Number(extremeValue.toFixed(5));
}

export function isOutsideElement(target: EventTarget | null, element: HTMLElement | null | undefined): boolean {
  return Boolean(target && element && !element.contains(target as Node));
}

export function hasTextValue(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

export function clearTruthyFormControls(form: UntypedFormGroup, controlNames: string[]): void {
  for (const controlName of controlNames) {
    const control = form.get(controlName);
    if (control?.value) {
      control.patchValue(false, { emitEvent: false });
    }
  }
}

export function applyPreviousValueMode(form: UntypedFormGroup, mode: 'copy' | 'carry'): void {
  form.patchValue(
    {
      copy_previous_block_value: mode === 'copy',
      carry_over_previous_day_values: mode === 'carry',
    },
    { emitEvent: true },
  );
}

export function shouldFetchPreviousValue(
  form: UntypedFormGroup,
  selectedControlName: string,
  resetControlName: string,
  hasContext: boolean,
  disabled = false,
): boolean {
  const selectedControl = form.get(selectedControlName);
  if (!selectedControl?.value || !hasContext || disabled) {
    return false;
  }

  form.patchValue({ [resetControlName]: false }, { emitEvent: false });
  return true;
}

export function normalizeReportSearchQuery(query: unknown): string {
  return typeof query === 'string' ? query.trim().toLowerCase() : '';
}

export function filterReportOptions<T extends ReportOptionLike>(options: T[], query: string): T[] {
  const normalizedQuery = normalizeReportSearchQuery(query);
  if (!normalizedQuery) {
    return [...options];
  }

  return options.filter((option) => {
    const name = (option.name ?? '').toLowerCase();
    const code = (option.station_code ?? '').toLowerCase();
    return name.includes(normalizedQuery) || code.includes(normalizedQuery);
  });
}

export function findReportOptionByLabel<T extends ReportOptionLike>(filteredOptions: T[], allOptions: T[], label: string): T | null {
  const normalizedLabel = normalizeReportSearchQuery(label);
  if (!normalizedLabel) {
    return null;
  }

  return (
    filteredOptions.find((option) => (option.name ?? '').trim().toLowerCase() === normalizedLabel) ??
    allOptions.find((option) => (option.name ?? '').trim().toLowerCase() === normalizedLabel) ??
    null
  );
}

export function clearNumericFormControlIfSelected(
  form: UntypedFormGroup,
  controlName: string,
  normalizeNumber: NormalizeNumberFn,
  emitEvent = true,
): void {
  if (normalizeNumber(form.get(controlName)?.value) !== null) {
    form.patchValue({ [controlName]: null }, { emitEvent });
  }
}

export function patchNumericFormControlIfChanged(
  form: UntypedFormGroup,
  controlName: string,
  value: number,
  normalizeNumber: NormalizeNumberFn,
  emitEvent = true,
): void {
  if (normalizeNumber(form.get(controlName)?.value) !== value) {
    form.patchValue({ [controlName]: value }, { emitEvent });
  }
}

export function handleReportAutocompleteSearch<TOption extends ReportOptionLike>(config: ReportAutocompleteSearchConfig<TOption>): void {
  const query = typeof config.query === 'string' ? config.query : '';
  config.filterOptions(query);

  const normalizedQuery = query.trim();
  if (!normalizedQuery) {
    clearNumericFormControlIfSelected(config.form, config.controlName, config.normalizeNumber);
    return;
  }

  const match = config.findOption(normalizedQuery);
  if (match?.id) {
    patchNumericFormControlIfChanged(config.form, config.controlName, Number(match.id), config.normalizeNumber);
  }
}

export function commitReportAutocompleteValue<TOption extends ReportOptionLike>(config: ReportAutocompleteCommitConfig<TOption>): void {
  const currentValue = typeof config.searchTerm === 'string' ? config.searchTerm.trim() : '';
  if (!currentValue) {
    clearNumericFormControlIfSelected(config.form, config.controlName, config.normalizeNumber);
    return;
  }

  const match = config.findOption(currentValue);
  if (!match?.id) {
    clearNumericFormControlIfSelected(config.form, config.controlName, config.normalizeNumber);
    return;
  }

  config.setSearchTerm(match.name ?? '');
  patchNumericFormControlIfChanged(config.form, config.controlName, Number(match.id), config.normalizeNumber);
}

export function stripTrailingParenthesizedText(value: string): string {
  if (!value.endsWith(')')) {
    return value;
  }

  const openingIndex = value.lastIndexOf('(');
  return openingIndex > 0 ? value.slice(0, openingIndex).trimEnd() : value;
}

export function stripTotalReportLabel(value: string | null | undefined): string {
  const candidate = typeof value === 'string' ? value.trim() : '';
  return stripTrailingParenthesizedText(candidate).replace(/^total\s+/i, '').trim();
}

export function slugifyReportLabel(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

export function getReportHierarchyNodeOrderKey(row: ReportHierarchyOrderRowLike): string {
  if (row.row_kind === 'sector-total') {
    return `sector:${slugifyReportLabel(stripTotalReportLabel(row.station_name))}`;
  }

  return `row:${row.row_key ?? row.row_kind ?? ''}`;
}

export function buildReportBlockGroups<TBlock extends ReportBlockDefinitionLike, TPeriod extends string>(
  blocks: TBlock[],
  getPeriodTitle: (period: TPeriod) => string,
): ReportBlockGroupLike<TPeriod>[] {
  const groups: ReportBlockGroupLike<TPeriod>[] = [];

  for (const block of blocks) {
    const period = block.period as TPeriod;
    const lastGroup = groups[groups.length - 1] ?? null;
    if (lastGroup && lastGroup.period === period) {
      lastGroup.span += 1;
      continue;
    }

    groups.push({
      period,
      label: getPeriodTitle(period),
      span: 1,
    });
  }

  return groups;
}

export function downloadReportBlob(blob: Blob, fileName: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
}

export function extractReportResponseValue(response: unknown): Record<string, unknown> | null {
  if (!response || typeof response !== 'object') {
    return null;
  }

  const root = getReportRecord(response);
  if (Array.isArray(root['blocks']) || Array.isArray(root['rows'])) {
    return root;
  }

  const value = root['value'] ?? root['data'];
  return value && typeof value === 'object' ? getReportRecord(value) : null;
}

export function getReportArrayValue(value: unknown, key: string): unknown[] {
  const candidate = getReportRecordValue(value, key);
  return Array.isArray(candidate) ? candidate : [];
}

export function getReportRecordValue(value: unknown, key: string): unknown {
  const record = getReportRecord(value);
  return record[key] ?? null;
}

export function getReportRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object') {
    return {};
  }

  return { ...(value as Record<string, unknown>) };
}

export function extractReportStringValue(value: unknown, key: string): string | null {
  const candidate = getReportRecordValue(value, key);
  if (typeof candidate !== 'string') {
    return null;
  }
  const trimmed = candidate.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function normalizeReportNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

export function extractReportDateConversion(response: unknown): ReportDateConversionLike | null {
  const value = extractReportResponseValue(response);
  if (!value) {
    return null;
  }

  return {
    data_date: extractReportStringValue(value, 'data_date') ?? '',
    bs_date: extractReportStringValue(value, 'bs_date') ?? '',
    bs_date_label: extractReportStringValue(value, 'bs_date_label') ?? '',
    selected_date: extractReportStringValue(value, 'selected_date') ?? '',
    selected_date_label: extractReportStringValue(value, 'selected_date_label') ?? '',
  };
}

export function extractReportBsCalendar(response: unknown): ReportBsCalendarLike | null {
  const value = extractReportResponseValue(response);
  if (!value) {
    return null;
  }

  const cells = getReportArrayValue(value, 'cells').map((item) => {
    const record = getReportRecord(item);
    return {
      bs_date: extractReportStringValue(record, 'bs_date') ?? '',
      bs_date_label: extractReportStringValue(record, 'bs_date_label') ?? '',
      ad_date: extractReportStringValue(record, 'ad_date') ?? '',
      in_current_month: Boolean(record['in_current_month']),
      is_today: Boolean(record['is_today']),
      is_selected: Boolean(record['is_selected']),
    };
  });

  return {
    data_date: extractReportStringValue(value, 'data_date') ?? '',
    bs_date: extractReportStringValue(value, 'bs_date') ?? '',
    bs_year: normalizeReportNumber(value['bs_year']) ?? 0,
    bs_month: normalizeReportNumber(value['bs_month']) ?? 0,
    bs_month_name: extractReportStringValue(value, 'bs_month_name') ?? '',
    selected_date: extractReportStringValue(value, 'selected_date') ?? '',
    selected_date_label: extractReportStringValue(value, 'selected_date_label') ?? '',
    cells,
  };
}
